import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import usePageTitle from "@/hooks/usePageTitle";
import CommitteeCatalogSection from "@/pages/people/membership-services/committees/_components/CommitteeCatalogSection";
import { SECTION_DETAILS_HEADER_QUERY, type SectionFragment } from "../queries";
import { useCanManageSectionCommittees } from "./useCanManageSectionCommittees";
import { useSectionOperationsAccess } from "./useSectionOperationsAccess";

export default function SectionOperationsCommitteesPage() {
  const { sectionId } = useParams();
  const { ready, showCommittees, showSectionOfficeFacilityTabs } = useSectionOperationsAccess(sectionId);
  const { data, previousData } = useQuery<SectionFragment>(SECTION_DETAILS_HEADER_QUERY, {
    variables: { sectionId },
    skip: !sectionId,
    fetchPolicy: "cache-first"
  });
  const cachedSection = previousData?.section;
  const section = data?.section ?? (cachedSection?.id === sectionId ? cachedSection : undefined);
  const sectionLegacyId = section?.legacyId;
  const sectionName = section?.name;
  const { canManageCommittees, ready: sectionManageReady } = useCanManageSectionCommittees(sectionLegacyId);

  usePageTitle(`/sections/${sectionId}/operations/committees`, "Sections | Operations | Committees");

  if (!sectionId || !ready || !sectionManageReady) {
    return null;
  }

  if (!showCommittees) {
    if (showSectionOfficeFacilityTabs) {
      return <Navigate to={`/sections/${sectionId}/operations/employees`} replace />;
    }
    return <Navigate to={`/sections/${sectionId}/jobs`} replace />;
  }

  return (
    <div className="w-full" data-testid="section-operations-committees">
      <CommitteeCatalogSection
        catalogTab="all"
        heading="Committees"
        body="View this section's committees with active, inactive, and total participation counts."
        sectionId={sectionId}
        sectionName={sectionName}
        canManageCommittees={canManageCommittees}
        committeeScope="section"
      />
    </div>
  );
}
